/**
 * @file theme.js
 * @description LeadifyAI MUI theme — built exclusively from design tokens.
 *
 * This file has one job: translate tokens into a MUI theme object.
 * It must not contain any component logic or business rules.
 *
 * Consumption flow:
 *  tokens.js  →  theme.js  →  App.js (passes as baseTheme)
 *                          →  SimpleAccessibilityContext.js (merges with
 *                             accessibility overrides, then applies via
 *                             ThemeProvider)
 *
 * Component override philosophy:
 *  - Shape:  use radii.*
 *  - Color:  use colors.*  (never raw hex literals here)
 *  - Motion: use transitions.*
 *  - Shadow: use shadows.*
 */
import { createTheme } from '@mui/material/styles';
import { colors, typography, radii, shadows, transitions } from './tokens';

// =============================================================================
// THEME
// =============================================================================

const theme = createTheme({

  // ---------------------------------------------------------------------------
  // Palette
  // ---------------------------------------------------------------------------
  palette: {
    primary: {
      main:          colors.primary.main,
      light:         colors.primary.light,
      dark:          colors.primary.dark,
      contrastText:  colors.background.default,
    },
    secondary: {
      // Neutral gray for secondary UI elements (not sky-blue, which is decorative)
      main:         colors.neutral[600],
      light:        colors.neutral[500],
      dark:         colors.neutral[700],
      contrastText: colors.background.default,
    },
    // Custom neutral scale — reachable via theme.palette.neutral[N]
    neutral: colors.neutral,
    background: {
      default: colors.background.default,
      paper:   colors.background.paper,
    },
    text: {
      primary:   colors.text.primary,
      secondary: colors.text.secondary,
    },
    error:   { main: colors.error.main },
    success: { main: colors.success.main },
    warning: { main: colors.warning.main },
    info:    { main: colors.info.main },
  },

  // ---------------------------------------------------------------------------
  // Typography
  // ---------------------------------------------------------------------------
  typography: {
    fontFamily: typography.fontFamily.body,

    h1: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.bold,
      fontSize:   '32px',
      color:      colors.text.heading,
    },
    h2: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.bold,
      fontSize:   '28px',
      color:      colors.text.heading,
    },
    h3: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.bold,
      fontSize:   '24px',
      color:      colors.text.heading,
    },
    h4: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.semiBold,
      fontSize:   '20px',
      color:      colors.text.heading,
    },
    h5: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.semiBold,
      fontSize:   '18px',
      color:      colors.text.heading,
    },
    h6: {
      fontFamily: typography.fontFamily.heading,
      fontWeight: typography.fontWeight.semiBold,
      fontSize:   '16px',
      color:      colors.text.heading,
    },
    body1: {
      fontSize:   '16px',
      lineHeight: typography.lineHeight.relaxed,
    },
    body2: {
      fontSize:   '14px',
      lineHeight: typography.lineHeight.base,
    },
    button: {
      textTransform: 'none',
      fontWeight:    typography.fontWeight.semiBold,
      fontSize:      '16px',
    },
  },

  // ---------------------------------------------------------------------------
  // Shape
  // ---------------------------------------------------------------------------
  shape: {
    // MUI uses this as the base multiplier for its border-radius scale.
    // Setting it to radii.md (12px) makes theme.shape.borderRadius === 12.
    borderRadius: radii.md,
  },

  // ---------------------------------------------------------------------------
  // Component overrides
  // All values sourced from tokens — no raw hex literals.
  // ---------------------------------------------------------------------------
  components: {

    // -------------------------------------------------------------------------
    // MuiButton
    // -------------------------------------------------------------------------
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius:  `${radii.md}px`,
          padding:       '10px 24px',
          fontSize:      '16px',
          fontWeight:    typography.fontWeight.semiBold,
          transition:    `all ${transitions.base}`,
          textTransform: 'none',
        },
        contained: {
          boxShadow:  shadows.primaryGlow,
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, #48D1CC 100%)`,
          '&:hover': {
            boxShadow:  shadows.primaryGlowHover,
            background: `linear-gradient(135deg, #48D1CC 0%, ${colors.primary.main} 100%)`,
            transform:  'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: colors.info.main,
          color:       colors.info.main,
          '&:hover': {
            borderWidth:     '2px',
            borderColor:     colors.info.hover,
            backgroundColor: colors.info.alpha08,
          },
        },
        text: {
          color: colors.info.main,
          '&:hover': {
            backgroundColor: colors.info.alpha08,
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTextField
    // -------------------------------------------------------------------------
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius:    `${radii.md}px`,
            backgroundColor: colors.background.default,
            transition:      `all ${transitions.base}`,
            '& fieldset': {
              borderColor: colors.neutral[300],
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: colors.neutral[400],
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '1px',
              boxShadow:   `0 0 0 3px ${colors.primary.alpha10}`,
            },
            '&.Mui-focused': {
              outline: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text.label,
            '&.Mui-focused': {
              color: colors.primary.main,
            },
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiCard
    // -------------------------------------------------------------------------
    MuiCard: {
      styleOverrides: {
        // Callback form required for breakpoints inside styleOverrides.
        // MuiCard override is injected AFTER MuiPaper in the stylesheet,
        // so these rules win over the Paper default borderRadius.
        root: ({ theme }) => ({
          borderRadius: radii.card.xs,
          [theme.breakpoints.up('sm')]: {
            borderRadius: radii.card.sm,
          },
          boxShadow:       shadows.card,
          border:          `1px solid ${colors.primary.alpha10}`,
          backgroundColor: colors.background.default,
          transition:      `all ${transitions.base}`,
          '&:hover': {
            boxShadow: shadows.cardHover,
            transform: 'translateY(-2px)',
          },
        }),
      },
    },

    // -------------------------------------------------------------------------
    // MuiPaper
    // -------------------------------------------------------------------------
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: `${radii.lg}px`,
          boxShadow:    shadows.paper,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiAppBar
    // -------------------------------------------------------------------------
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.default,
          color:           colors.text.heading,
          boxShadow:       shadows.paper,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTab / MuiTabs
    // -------------------------------------------------------------------------
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight:    typography.fontWeight.semiBold,
          fontSize:      '16px',
          color:         colors.text.label,
          '&.Mui-selected': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.primary.main,
          height:          '3px',
          borderRadius:    '3px 3px 0 0',
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiCheckbox / MuiRadio / MuiSwitch
    // -------------------------------------------------------------------------
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.neutral[500],
          '&.Mui-checked': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.neutral[500],
          '&.Mui-checked': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase': {
            color: colors.neutral[400],
          },
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: colors.primary.main,
            '&:hover': {
              backgroundColor: colors.primary.alpha08,
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: colors.neutral[300],
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.primary.main,
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiChip
    // -------------------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: `${radii.md}px`,
          fontWeight:   typography.fontWeight.medium,
        },
        colorPrimary: {
          backgroundColor: colors.primary.alpha10,
          color:           colors.primary.main,
        },
        colorSecondary: {
          backgroundColor: colors.info.alpha12,
          color:           colors.info.main,
          border:          `1px solid ${colors.info.alpha30}`,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiAlert
    // -------------------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: `${radii.md}px`,
        },
        standardSuccess: {
          backgroundColor: colors.success.alpha12,
          color:           colors.success.dark,
          '& .MuiAlert-icon': {
            color: colors.success.main,
          },
        },
        standardError: {
          backgroundColor: colors.error.alpha10,
        },
        standardWarning: {
          backgroundColor: colors.warning.alpha12,
          color:           colors.warning.dark,
          '& .MuiAlert-icon': {
            color: colors.warning.main,
          },
        },
        standardInfo: {
          backgroundColor: colors.info.alpha12,
          color:           colors.info.dark,
          '& .MuiAlert-icon': {
            color: colors.info.main,
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiLinearProgress
    // -------------------------------------------------------------------------
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height:          '8px',
          borderRadius:    '4px',
          backgroundColor: colors.primary.alpha10,
        },
        bar: {
          borderRadius: '4px',
          background:   `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiCircularProgress
    // -------------------------------------------------------------------------
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiLink
    // -------------------------------------------------------------------------
    MuiLink: {
      styleOverrides: {
        root: {
          color:          colors.info.main,
          textDecoration: 'none',
          '&:hover': {
            color:          colors.info.hover,
            textDecoration: 'underline',
          },
        },
      },
    },

  }, // end components
});

export default theme;
